import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDownToLine, ArrowUpFromLine, DollarSign } from "lucide-react";

export type UsagePeriod = "day" | "week" | "month";

const PERIOD_HOURS: Record<UsagePeriod, number> = {
  day: 24,
  week: 24 * 7,
  month: 24 * 30,
};

type Row = {
  group_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost_usd: number | null;
};

type GroupUsage = {
  input: number;
  output: number;
  cost: number;
};

export function useGroupsUsage(period: UsagePeriod, enabled = true) {
  return useQuery({
    queryKey: ["groups-usage", period],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const sinceIso = new Date(
        Date.now() - PERIOD_HOURS[period] * 60 * 60 * 1000,
      ).toISOString();
      const { data, error } = await supabase
        .from("model_decisions")
        .select("group_id, input_tokens, output_tokens, estimated_cost_usd")
        .gte("created_at", sinceIso)
        .not("group_id", "is", null)
        .limit(10000);
      if (error) throw error;
      const map = new Map<string, GroupUsage>();
      for (const r of (data ?? []) as Row[]) {
        if (!r.group_id) continue;
        const cur = map.get(r.group_id) ?? { input: 0, output: 0, cost: 0 };
        cur.input += r.input_tokens ?? 0;
        cur.output += r.output_tokens ?? 0;
        cur.cost += Number(r.estimated_cost_usd ?? 0);
        map.set(r.group_id, cur);
      }
      return map;
    },
  });
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function fmtCost(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

interface Props {
  groupId: string;
  usage?: GroupUsage;
  loading?: boolean;
}

export function GroupUsagePanel({ usage, loading }: Props) {
  const data = useMemo<GroupUsage>(
    () => usage ?? { input: 0, output: 0, cost: 0 },
    [usage],
  );

  if (loading) {
    return <Skeleton className="h-12 w-full rounded-md" />;
  }

  const empty = data.input === 0 && data.output === 0 && data.cost === 0;

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <Tile
          icon={<ArrowDownToLine className="w-3 h-3" />}
          label="Input"
          value={fmtTokens(data.input)}
        />
        <div className="h-6 w-px bg-border/60" />
        <Tile
          icon={<ArrowUpFromLine className="w-3 h-3" />}
          label="Output"
          value={fmtTokens(data.output)}
        />
        <div className="h-6 w-px bg-border/60" />
        <Tile
          icon={<DollarSign className="w-3 h-3" />}
          label="Custo"
          value={fmtCost(data.cost)}
          highlight
        />
      </div>
      {empty && (
        <p className="text-[10px] text-muted-foreground mt-1 text-center">
          sem chamadas no período
        </p>
      )}
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col items-center min-w-0 flex-1">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </div>
      <div
        className={`text-xs font-semibold tabular-nums ${highlight ? "text-primary" : "text-foreground"}`}
      >
        {value}
      </div>
    </div>
  );
}
